
import { VMoney } from 'v-money'
import { mapActions } from 'vuex'
import ExternalSeriesTable from '@/components/ExternalSeriesTable.vue'
import MediaPlanTable from '@/components/MediaPlanTable.vue'
import moment from 'moment'
import { effectiveModelStart } from '@/utils/modelStart.js'
import { weeklyPeriodDates } from '@/utils/weeklyPeriod.js'

export default {
  components: {
    ExternalSeriesTable,
    MediaPlanTable
  },
  data () {
    return {
      validForm: true,
      name: '',
      dateFrom: '',
      dateFromMenu: false,
      dateTo: '',
      dateToMenu: false,
      money: {
        thousands: ',',
        precision: 0
      },
      externalSeriesChanges: {},
      mediaPlan: {},
      mediaPlanWeeklyFrequency: false,
      externalSeriesWeeklyFrequency: false,
      weeklyInfoDialog: false
    }
  },
  computed: {
    rules () {
      return {
        name: [
          v => !!v || this.$t('components.CreateSimulation.nameRequired')
        ],
        dateFrom: [
          v => !!v || this.$t('components.CreateSimulation.dateRequired')
        ],
        dateTo: [
          v => !!v || this.$t('components.CreateSimulation.dateRequired'),
          v => v > this.dateFrom || this.$t('components.CreateSimulation.mustBeAfterStartDate')
        ]
      }
    },
    referenceModelConfig () {
      if (this.client.plan_reference_model) {
        return this.client.plan_reference_model.config
      } else {
        return this.client.reference_model.config
      }
    },
    prettyMediaNamesDict () {
      return this.client.pretty_names ? this.client.pretty_names.media : {}
    },
    modelFrequency: function () {
      if (this.referenceModelConfig.saturation_granularity === 'W') {
        // Monthly saturation_granularity implies a weekly-capable model
        return 'week'
      } else {
        return 'month'
      }
    },
    mediaPlanModelFrequency: function () {
      if (this.mediaPlanWeeklyFrequency === true) {
        return 'week'
      } else {
        return 'month'
      }
    },
    allowFrequencyToggle: function () {
      return !!(this.base && this.base.allow_frequency_toggle)
    },
    externalSeriesModelFrequency: function () {
      if (this.externalSeriesWeeklyFrequency === true) {
        return 'week'
      } else {
        return 'month'
      }
    },
    media: function () {
      let media = {}
      for (let channel in this.referenceModelConfig.media) {
        media[channel] = {}
        this.referenceModelConfig.media[channel].forEach(touchpoint => {
          media[channel][touchpoint] = `${channel}@div@${touchpoint}`
        })
      }
      return media
    },
    brand: function () {
      if (this.client.type === 'mmm' || !this.client.custom_model) {
        return Object.keys(this.referenceModelConfig.brands)[0]
      }
      return this.client.custom_model.brand
    },
    externalSeries: function () {
      if ('external_series' in this.referenceModelConfig.brands[this.brand]) {
        // Weighting rules according to this PRD:
        // https://docs.google.com/document/d/1o5CUGOAibNWBSvHEkZN0B7Wgh2bgiK8AIQtPdBcfpFE/
        // Code duplicated in CreateOptimization.vue
        const originalExternalSeries = this.referenceModelConfig.brands[this.brand].external_series
        let weightedExternalSeries = {}
        let competitionNames = []
        let comarketingNames = []
        let competitionWeight = 1
        let comarketingWeight = 1
        let referenceAverageMonthlyInvestCompetitionOverOne = true
        let referenceAverageMonthlyInvestComarketingOverOne = true
        if ('custom_model' in this.client && 'competition_names' in this.referenceModelConfig.brands[this.brand] && 'average_monthly_invest_competition' in this.referenceModelConfig.brands[this.brand]) {
          // Only apply weighting to Custom Models with competition_names and average_monthly_invest_competition
          // If referenceAverageMonthlyInvestCompetition < 1, apply static weighting
          const referenceAverageMonthlyInvestCompetition = this.referenceModelConfig.brands[this.brand].average_monthly_invest_competition
          const customAverageMonthlyInvestCompetition = this.client.custom_model.average_monthly_invest_competition
          competitionNames = this.referenceModelConfig.brands[this.brand].competition_names
          if (referenceAverageMonthlyInvestCompetition >= 1) {
            competitionWeight = customAverageMonthlyInvestCompetition / referenceAverageMonthlyInvestCompetition
          } else {
            referenceAverageMonthlyInvestCompetitionOverOne = false
            competitionWeight = 5 * customAverageMonthlyInvestCompetition
          }
        }
        if ('custom_model' in this.client && 'comarketing_names' in this.referenceModelConfig.brands[this.brand] && 'average_monthly_invest_comarketing' in this.referenceModelConfig.brands[this.brand]) {
          // Only apply weighting to Custom Models with comarketing_names and average_monthly_invest_comarketing
          // If referenceAverageMonthlyInvestComarketing < 1, apply static weighting
          const referenceAverageMonthlyInvestComarketing = this.referenceModelConfig.brands[this.brand].average_monthly_invest_comarketing
          const customAverageMonthlyInvestComarketing = this.client.custom_model.average_monthly_invest_comarketing
          comarketingNames = this.referenceModelConfig.brands[this.brand].comarketing_names
          if (referenceAverageMonthlyInvestComarketing >= 1) {
            comarketingWeight = customAverageMonthlyInvestComarketing / referenceAverageMonthlyInvestComarketing
          } else {
            referenceAverageMonthlyInvestComarketingOverOne = false
            comarketingWeight = 5 * customAverageMonthlyInvestComarketing
          }
        }
        Object.entries(originalExternalSeries).forEach(series => {
          if (competitionNames.includes(series[0])) {
            // If referenceAverageMonthlyInvestCompetition < 1, only apply weighting to max
            weightedExternalSeries[series[0]] = {
              unit: series[1].unit,
              min: referenceAverageMonthlyInvestCompetitionOverOne ? Math.round(series[1].min * competitionWeight) : series[1].min,
              max: referenceAverageMonthlyInvestCompetitionOverOne ? Math.round(series[1].max * competitionWeight) : competitionWeight,
              default_values: series[1].default_values.map(value => {
                return referenceAverageMonthlyInvestCompetitionOverOne ? Math.round(value * competitionWeight) : value
              })
            }
          } else if (comarketingNames.includes(series[0])) {
            // If referenceAverageMonthlyInvestComarketing < 1, only apply weighting to max
            weightedExternalSeries[series[0]] = {
              unit: series[1].unit,
              min: referenceAverageMonthlyInvestComarketingOverOne ? Math.round(series[1].min * comarketingWeight) : series[1].min,
              max: referenceAverageMonthlyInvestComarketingOverOne ? Math.round(series[1].max * comarketingWeight) : comarketingWeight,
              default_values: series[1].default_values.map(value => {
                return referenceAverageMonthlyInvestComarketingOverOne ? Math.round(value * comarketingWeight) : value
              })
            }
          } else {
            // Skip weighting
            weightedExternalSeries[series[0]] = series[1]
          }
        })
        return weightedExternalSeries
      } else {
        return null
      }
    },
    startDate: function () {
      let date
      if ('dates' in this.referenceModelConfig.brands[this.brand]) {
        date = this.referenceModelConfig.brands[this.brand].dates.start
      } else {
        date = this.referenceModelConfig.dates.start
      }
      // Legacy weekly models (created before the cutoff) have an unreliable first
      // month of weekly data, so their effective start is pushed to the 1st of the
      // following month. See effectiveModelStart for the shared rule.
      return effectiveModelStart(date, this.modelFrequency, this.client.date_created.toDate())
    },
    endDate: function () {
      let originalEndDate
      if ('dates' in this.referenceModelConfig.brands[this.brand]) {
        originalEndDate = this.referenceModelConfig.brands[this.brand].dates.end
      } else {
        originalEndDate = this.referenceModelConfig.dates.end
      }

      // Limit EKRM-based models to 24 months from start date
      if (this.client.type === 'ekrm') {
        const startDate = this.startDate
        const maxEndDate = moment(startDate).add(24, 'months').subtract(1, 'day').format('YYYY-MM-DD')

        // Return the earlier date
        return originalEndDate < maxEndDate ? originalEndDate : maxEndDate
      }

      return originalEndDate
    },
    mediaPlanAllDates: function () {
      if (this.mediaPlanModelFrequency === 'month') {
        if ('dates' in this.referenceModelConfig.brands[this.brand]) {
          return this.referenceModelConfig.brands[this.brand].dates.all
        } else {
          return this.referenceModelConfig.dates.all
        }
      } else {
        return this.mediaPlanSelectedPeriod
      }
    },
    externalSeriesAllDates: function () {
      if (this.externalSeriesModelFrequency === 'month') {
        if ('dates' in this.referenceModelConfig.brands[this.brand]) {
          return this.referenceModelConfig.brands[this.brand].dates.all
        } else {
          return this.referenceModelConfig.dates.all
        }
      } else {
        return this.externalSeriesSelectedPeriod
      }
    },
    mediaPlanSelectedPeriodStart: function () {
      if (this.mediaPlanSelectedPeriod.length > 0 && this.mediaPlanModelFrequency === 'month') {
        return this.mediaPlanAllDates.indexOf(this.mediaPlanSelectedPeriod[0])
      } else {
        return 0
      }
    },
    externalSeriesSelectedPeriodStart: function () {
      if (this.externalSeriesSelectedPeriod.length > 0 && this.externalSeriesModelFrequency === 'month') {
        return this.externalSeriesAllDates.indexOf(this.externalSeriesSelectedPeriod[0])
      } else {
        return 0
      }
    },
    mediaPlanSelectedPeriodLength: function () {
      return this.mediaPlanSelectedPeriod.length
    },
    externalSeriesSelectedPeriodLength: function () {
      return this.externalSeriesSelectedPeriod.length
    },
    mediaPlanSelectedPeriod: function () {
      if (this.dateFrom && this.dateTo) {
        if (this.mediaPlanModelFrequency === 'month') {
          // Monthly model
          const fromSliced = this.dateFrom.slice(0, 7)
          const toSliced = this.dateTo.slice(0, 7)
          return this.mediaPlanAllDates.filter(date => {
            let dateSliced = date.slice(0, 7)
            if (dateSliced >= fromSliced && dateSliced <= toSliced) {
              return true
            } else {
              return false
            }
          })
        } else {
          // Weekly model. The axis is bounded by the week containing the
          // model's last available date, not by that date itself, so a
          // dates.end that isn't a Sunday still gets its (partial) week as a
          // column. See weeklyPeriodDates.
          return weeklyPeriodDates(this.dateFrom, this.dateTo, this.endDate)
        }
      } else {
        return []
      }
    },
    externalSeriesSelectedPeriod: function () {
      if (this.dateFrom && this.dateTo) {
        if (this.externalSeriesModelFrequency === 'month') {
          // Monthly model
          const fromSliced = this.dateFrom.slice(0, 7)
          const toSliced = this.dateTo.slice(0, 7)
          return this.externalSeriesAllDates.filter(date => {
            let dateSliced = date.slice(0, 7)
            if (dateSliced >= fromSliced && dateSliced <= toSliced) {
              return true
            } else {
              return false
            }
          })
        } else {
          // Weekly model. Same axis as the media plan, so both tables always
          // show the same weeks.
          return weeklyPeriodDates(this.dateFrom, this.dateTo, this.endDate)
        }
      } else {
        return []
      }
    },
    spendUnitName: function () {
      if ('invest_unit_name' in this.referenceModelConfig) {
        return this.referenceModelConfig.invest_unit_name
      } else {
        return 'USD'
      }
    },
    sortedExternalSeries: function () {
      if (!this.externalSeries) {
        return []
      }
      return Object.entries(this.externalSeries).sort((a, b) => a[0].localeCompare(b[0]))
    }
  },
  methods: {
    ...mapActions('simulations', [
      'addSimulation',
      'editSimulation'
    ]),
    closeDialog () {
      this.$emit('close-dialog')
    },
    validateForm () {
      return this.$refs.form.validate()
    },
    createSimulation () {
      if (this.validateForm()) {
        var action = this.addSimulation
        if (this.edit) {
          action = this.editSimulation
        }
        action({
          client: this.client.id,
          user: `${this.user.firstName} ${this.user.lastName}`,
          userId: this.user.uid,
          baseSimulation: this.base ? this.base.id : 'none',
          simulation: {
            name: this.name,
            date_from: this.dateFrom,
            date_to: this.dateTo,
            budget: this.mediaPlanTotal(),
            external_series_changes: this.convertExternalSeriesChangesToNumbers(),
            media_plan: this.formattedMediaPlan(),
            type: 'plan_simulation',
            media_plan_frequency: this.mediaPlanModelFrequency,
            external_series_frequency: this.externalSeriesModelFrequency
          }
        })
        this.closeDialog()
      }
    },
    removeNonDigits (str) {
      if (str) {
        return str.toString().replace(/\D/g, '')
      } else {
        return ''
      }
    },
    convertExternalSeriesChangesToNumbers () {
      let converted = {}
      Object.keys(this.externalSeriesChanges).forEach(key => {
        converted[key] = this.externalSeriesChanges[key].map(text => {
          if (text !== null) {
            return Number(text)
          } else {
            return null
          }
        })
      })
      return converted
    },
    formattedMediaPlan: function () {
      let mediaPlan = {}
      const channels = Object.keys(this.channels)
      channels.forEach(channel => {
        mediaPlan[channel] = []
      })
      const dates = this.mediaPlanSelectedPeriod.sort()
      dates.forEach(date => {
        channels.forEach(channel => {
          if (date in this.mediaPlan && channel in this.mediaPlan[date]) {
            mediaPlan[channel].push(parseInt(this.mediaPlan[date][channel]))
          } else {
            mediaPlan[channel].push(0)
          }
        })
      })
      return mediaPlan
    },
    mediaPlanTotal: function () {
      let total = 0
      for (let date in this.mediaPlan) {
        for (let medium in this.mediaPlan[date]) {
          const value = parseInt(this.mediaPlan[date][medium])
          if (!isNaN(value)) {
            total += value
          }
        }
      }
      return total
    }
  },
  watch: {
    mediaPlanSelectedPeriodLength: function (after, before) {
      // Reset everything related to mediaPlanSelectedPeriodLength when changing the periodLength manually only
      /* if (before > 0) {
        this.mediaPlan = {}
      } */
      Object.keys(this.mediaPlan).forEach(date => {
        if (!this.mediaPlanSelectedPeriod.includes(date)) {
          delete this.mediaPlan[date]
        }
      })
      this.mediaPlanSelectedPeriod.forEach(date => {
        if (!this.mediaPlan[date]) {
          this.mediaPlan[date] = {}
          Object.keys(this.channels).forEach(channel => {
            this.mediaPlan[date][channel] = 0
          })
        }
      })
    },
    externalSeriesSelectedPeriodLength: function (after, before) {
      // Reset everything related to externalSeriesSelectedPeriodLength when changing the periodLength manually only
      if (before > 0) {
        this.externalSeriesChanges = {}
      }
    }
  },
  directives: {
    money: VMoney
  },
  props: {
    channels: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    base: {
      type: Object,
      required: false
    },
    edit: {
      type: Boolean,
      required: false
    },
    isTemplate: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  created: function () {
    if (this.base) {
      if (this.base.media_plan_frequency && this.base.media_plan_frequency === 'week') {
        this.mediaPlanWeeklyFrequency = true
      }
      if (this.base.external_series_frequency && this.base.external_series_frequency === 'week') {
        this.externalSeriesWeeklyFrequency = true
      }
      this.name = this.base.name + ((this.edit || this.isTemplate) ? '' : ' (copy)')
      this.dateFrom = this.base.date_from
      this.dateTo = this.base.date_to
      if (this.base.media_plan) {
        let date = null
        for (let index in this.mediaPlanSelectedPeriod) {
          date = this.mediaPlanSelectedPeriod[index]
          if (!this.mediaPlan[date]) {
            this.$set(this.mediaPlan, date, {})
          }
          for (let key in this.base.media_plan) {
            // Plans saved before the weekly axis included the week containing
            // dates.end are one value short of the current period, so a
            // missing value falls back to 0 (same as formattedMediaPlan does
            // for dates with no data) instead of throwing on undefined.
            const value = this.base.media_plan[key][index]
            this.$set(this.mediaPlan[date], key, (value === undefined || value === null) ? '0' : value.toString())
          }
        }
      }
      if (this.base.external_series_changes) {
        for (let key in this.base.external_series_changes) {
          this.externalSeriesChanges[key] = [...this.base.external_series_changes[key]]
        }
      }
    } else {
      // Default to the model's native granularity when creating from scratch
      if (this.modelFrequency === 'week') {
        this.mediaPlanWeeklyFrequency = true
      }
      // Set default Start and End dates
      const today = new Date().toISOString().substr(0, 10)
      const endDate = this.endDate
      const startDate = this.startDate
      if (today > endDate || today < startDate) {
        const firstDayDate = startDate.substring(0, 8) + '01'
        this.dateFrom = firstDayDate
        this.dateTo = firstDayDate
      } else {
        this.dateFrom = today
        this.dateTo = today
      }
    }
  }
}
